import { GridColDef } from '@mui/x-data-grid-pro';
import moment from 'moment/moment';

import {
  OpenInNewTabAction,
  canOpenPath,
} from '@/components/OpenInNewTabAction';
import { DATE_FORMAT, DATE_TIME_FORMAT } from '@/config';
import { KaseStatusChip } from '@/layouts/TabLayout/MasterNames/styles';
import {
  Disposition,
  DispositionFriendlyNames,
  DispositionReasons,
  ProcessType,
  ProcessTypeFriendlyNames,
} from '@/models';
import { getAddressLocation } from '@/services';
import { useUserStore } from '@/store';
import { formatDateOnly } from '@/utils/datetime';

const involvementCell = (): Partial<GridColDef> => ({
  sortable: false,
  renderCell: (params) => {
    const value =
      params.row.relationship?.involvement ?? params.row.involvement;
    if (!value) return '/';
    const arr = Array.isArray(value) ? value : [value];
    return arr.map((item: string, index: number) => (
      <KaseStatusChip key={`${item}-${index}`} label={item} />
    ));
  },
});

const addressCell = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    getAddressLocation(params.row.address) || 'No Address',
  valueGetter: (_, row) => row.fullAddressSortable || '',
});

// --- CFS columns ---

export const personCfsCols = () => ({
  cfsId: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.name,
  }),
  location: addressCell,
  dateAndTime: (): Partial<GridColDef> => ({
    renderCell: (params) =>
      moment(params.row.createdAt).format(DATE_TIME_FORMAT) || '/',
    valueGetter: (_, row) => row.createdAt,
  }),
  incidentCode: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.incidentCode || '/',
  }),
  involvement: involvementCell,
  actions: (): Partial<GridColDef> => ({
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const { user, account } = useUserStore();
      const path = `/cfs/${params.row._id}?newTab=true`;
      return (
        <OpenInNewTabAction
          path={path}
          canOpen={canOpenPath(path, user, account)}
        />
      );
    },
  }),
});

// --- Arrest columns ---

export const personArrestCols = () => ({
  number: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.number || '/',
  }),
  arrestId: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.arrestId || '/',
  }),
  masterName: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.fullName || '/',
  }),
  arrestDate: (): Partial<GridColDef> => ({
    renderCell: (params) =>
      params.row.arrestDate
        ? moment(params.row.arrestDate).format(DATE_FORMAT)
        : '/',
  }),
  arrestType: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.arrestType || '/',
  }),
  arrestStatus: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.arrestStatus || '/',
  }),
  dataWorksStatus: (): Partial<GridColDef> => ({
    renderCell: (params) => {
      const STATUS_MAP: Record<string, string> = {
        CREATED: 'Created',
        IN_TEMP: 'In Temp',
        READY_FOR_BOOKING: 'Ready for Booking',
        PROCESSED: 'Processed',
      };
      const status = params.row.dataWorksStatus;
      return status ? STATUS_MAP[status] || status : '/';
    },
  }),
  actions: (): Partial<GridColDef> => ({
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const { user, account } = useUserStore();
      const path = `/cases/${params.row.kaseId}/arrests`;
      return (
        <OpenInNewTabAction
          path={path}
          canOpen={canOpenPath(path, user, account)}
        />
      );
    },
  }),
});

// --- Case columns ---

export const personCaseCols = () => ({
  caseId: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.name,
  }),
  location: addressCell,
  dateAndTime: (): Partial<GridColDef> => ({
    renderCell: (params) =>
      moment(params.row.createdAt).format(DATE_TIME_FORMAT) || '/',
    valueGetter: (_, row) => row.createdAt,
  }),
  status: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.status || '/',
  }),
  disposition: (): Partial<GridColDef> => ({
    renderCell: (params) => {
      const code = params.row.disposition;
      if (!code) return '/';
      const match = DispositionReasons.find((r) => r.value === code);
      return match?.label || code;
    },
  }),
  involvement: involvementCell,
  actions: (): Partial<GridColDef> => ({
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const { user, account } = useUserStore();
      const path = `/cases/${params.row._id}/overview`;
      return (
        <OpenInNewTabAction
          path={path}
          canOpen={canOpenPath(path, user, account)}
        />
      );
    },
  }),
});

// --- Warrant columns ---

export const personWarrantCols = () => ({
  warrantId: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.recordNumber || '/',
  }),
  processType: (): Partial<GridColDef> => ({
    renderCell: (params) =>
      ProcessTypeFriendlyNames[params.row.processType as ProcessType] ||
      params.row.processType ||
      '/',
  }),
  involvement: (): Partial<GridColDef> => ({
    sortable: false,
    renderCell: (params) => {
      const value = params.row.involvement;
      if (!value) return '/';
      return <KaseStatusChip label={value} />;
    },
  }),
  disposition: (): Partial<GridColDef> => ({
    renderCell: (params) =>
      DispositionFriendlyNames[params.row.disposition as Disposition] ||
      params.row.disposition ||
      '/',
  }),
  dateIssued: (): Partial<GridColDef> => ({
    renderCell: (params) =>
      params.row.dateIssued ? formatDateOnly(params.row.dateIssued) : '/',
  }),
  actions: (): Partial<GridColDef> => ({
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const { user, account } = useUserStore();
      const path = `/tools/wanted-person/view/${params.row._id}`;
      return (
        <OpenInNewTabAction
          path={path}
          canOpen={canOpenPath(path, user, account)}
        />
      );
    },
  }),
});

// --- Task columns ---

export const personTaskCols = () => ({
  taskId: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.taskID || '/',
  }),
  taskType: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.taskTypeName || '/',
  }),
  status: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.statusName || '/',
  }),
  dateAndTime: (): Partial<GridColDef> => ({
    renderCell: (params) =>
      moment(params.row.createdAt).format(DATE_TIME_FORMAT) || '/',
    valueGetter: (_, row) => row.createdAt,
  }),
  actions: (): Partial<GridColDef> => ({
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const { user, account } = useUserStore();
      const path = `/tasks/${params.row._id}`;
      return (
        <OpenInNewTabAction
          path={path}
          canOpen={canOpenPath(path, user, account)}
        />
      );
    },
  }),
});

// --- DIR columns ---

export const personDirCols = () => ({
  dirId: (): Partial<GridColDef> => ({
    renderCell: (params) => params.row.dirId || '/',
  }),
  location: addressCell,
  dateAndTime: (): Partial<GridColDef> => ({
    renderCell: (params) =>
      moment(params.row.createdAt).format(DATE_TIME_FORMAT) || '/',
    valueGetter: (_, row) => row.createdAt,
  }),
  involvement: (): Partial<GridColDef> => ({
    sortable: false,
    renderCell: (params) => {
      const value = params.row.involvement;
      if (!value) return '/';
      return <KaseStatusChip label={value} />;
    },
  }),
  actions: (): Partial<GridColDef> => ({
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const { user, account } = useUserStore();
      const path = `/cases/${params.row.kase}/overview`;
      if (!params.row.kase) return null;
      return (
        <OpenInNewTabAction
          path={path}
          canOpen={canOpenPath(path, user, account)}
        />
      );
    },
  }),
});
